import React, { useState, useEffect } from 'react';
import { targetAppStore, TargetAppState } from './store';
import { ProductGrid } from './components/ProductGrid';
import { CartSidebar } from './components/CartSidebar';
import { CheckoutForm } from './components/CheckoutForm';
import { DiscountEngine } from './components/DiscountEngine';
import { OrderHistory } from './components/OrderHistory';
import { ShoppingBag, RefreshCw, AlertOctagon, Wrench, Layers } from 'lucide-react';
import { CalculatorApp } from './CalculatorApp';
import { CustomPlayground } from '../components/ControlCenter/CustomPlayground';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onCrash: (error: Error, errorInfo: React.ErrorInfo) => void;
  isPatched: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class TargetAppErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onCrash(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // If patch applied, auto-reset the error boundary
    if (!prevProps.isPatched && this.props.isPatched && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  render() {
    if (this.state.hasError && !this.props.isPatched) {
      return (
        <div className="error-boundary animate-fade-in">
          <div style={{ background: 'var(--red-soft)', padding: 12, borderRadius: '50%', color: 'var(--red)' }}>
            <AlertOctagon size={32} />
          </div>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--red)' }}>
              Component Crash: Unhandled Exception in Render Tree
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-2)', maxWidth: 360, margin: '6px auto 0' }}>
              <code>{this.state.error?.message || 'TypeError: Cannot read properties of null'}</code>
            </p>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              background: 'rgba(0,0,0,0.4)',
              padding: '6px 12px',
              borderRadius: 6,
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--amber)',
            }}
          >
            <Wrench size={13} /> AutoFix WebMCP Agent is diagnosing live crash dump...
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const TargetApp: React.FC = () => {
  const [storeState, setStoreState] = useState<TargetAppState>(targetAppStore.getState());
  const [activeTab, setActiveTab] = useState<'shop' | 'orders'>('shop');
  const [selectedTarget, setSelectedTarget] = useState<'ecommerce' | 'calculator' | 'playground'>('ecommerce');

  useEffect(() => {
    const unsubscribe = targetAppStore.subscribe(() => {
      const current = targetAppStore.getState();
      setStoreState(current);
      if (current.isBug3Active && !current.runtimePatchesApplied['patch-bug-3']) {
        setActiveTab('orders');
      }
    });

    const handleSelectTarget = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && (detail === 'ecommerce' || detail === 'calculator' || detail === 'playground')) {
        setSelectedTarget(detail);
      }
    };

    window.addEventListener('autofix-select-target', handleSelectTarget);

    return () => {
      unsubscribe();
      window.removeEventListener('autofix-select-target', handleSelectTarget);
    };
  }, []);

  const handleCrashCaught = (error: Error, errorInfo: React.ErrorInfo) => {
    targetAppStore.setLastError(error.message);
    window.dispatchEvent(
      new CustomEvent('autofix-crash-detected', {
        detail: {
          bugId: 'bug-3',
          error,
          componentStack: errorInfo.componentStack,
          action: 'OrderHistory rendered with null orders state',
        },
      })
    );
  };

  const isBug1Patched = !!storeState.runtimePatchesApplied['patch-bug-1'];
  const isBug2Patched = !!storeState.runtimePatchesApplied['patch-bug-2'];
  const isBug3Patched = !!storeState.runtimePatchesApplied['patch-bug-3'];

  return (
    <div className="target-app" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Target Selector Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 14px',
          background: '#090d16',
          borderBottom: '1px solid var(--border)',
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Layers size={13} style={{ color: 'var(--accent)' }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.5px', color: 'var(--text-2)' }}>
            SELECT TARGET RUNTIME:
          </span>
        </div>

        <div className="tab-bar" style={{ padding: '2px 4px' }}>
          <button
            className={`tab ${selectedTarget === 'ecommerce' ? 'active' : ''}`}
            onClick={() => setSelectedTarget('ecommerce')}
            style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <span>🛒 CyberGear Store</span>
          </button>
          <button
            className={`tab ${selectedTarget === 'calculator' ? 'active' : ''}`}
            onClick={() => setSelectedTarget('calculator')}
            style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <span>🧮 React Calculator (2.7k ⭐)</span>
          </button>
          <button
            className={`tab ${selectedTarget === 'playground' ? 'active' : ''}`}
            onClick={() => setSelectedTarget('playground')}
            style={{ fontSize: 11, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <span>⚡ Custom AST Sandbox</span>
          </button>
        </div>
      </div>

      {/* Target Content based on selection */}
      {selectedTarget === 'calculator' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <CalculatorApp />
        </div>
      ) : selectedTarget === 'playground' ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          <CustomPlayground />
        </div>
      ) : (
        <>
          {/* Target App Header */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: 'var(--bg-panel)',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 6,
                  background: 'linear-gradient(135deg, var(--accent), #38bdf8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 14,
                }}
              >
                <ShoppingBag size={15} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-1)', lineHeight: 1.2 }}>
                  CyberGear Pro Store
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-3)' }}>
                  Live Target Runtime · Client E-Commerce App
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div className="tab-bar">
                <button
                  className={`tab ${activeTab === 'shop' ? 'active' : ''}`}
                  onClick={() => setActiveTab('shop')}
                >
                  Store & Checkout
                </button>
                <button
                  className={`tab ${activeTab === 'orders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('orders')}
                >
                  Order History {storeState.isBug3Active && !isBug3Patched && '⚠️'}
                </button>
              </div>

              <button
                className="btn btn-sm"
                onClick={() => targetAppStore.resetAll()}
                title="Reset Store State"
                style={{ padding: '4px 8px' }}
              >
                <RefreshCw size={11} /> Reset
              </button>
            </div>
          </div>

          {/* Target App Body */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
            <TargetAppErrorBoundary
              onCrash={handleCrashCaught}
              isPatched={isBug3Patched}
            >
              {activeTab === 'shop' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <ProductGrid products={storeState.products} />
                  <CartSidebar cart={storeState.cart} />
                  <DiscountEngine
                    appliedDiscounts={storeState.appliedDiscounts}
                    isBug2Active={storeState.isBug2Active}
                    isPatched={isBug2Patched}
                  />
                  <CheckoutForm
                    cart={storeState.cart}
                    appliedDiscounts={storeState.appliedDiscounts}
                    isBug1Active={storeState.isBug1Active}
                    isBug2Active={storeState.isBug2Active}
                    isPatched={isBug1Patched || isBug2Patched}
                  />
                </div>
              ) : (
                <OrderHistory
                  orders={storeState.orders}
                  isBug3Active={storeState.isBug3Active}
                  isPatched={isBug3Patched}
                />
              )}
            </TargetAppErrorBoundary>
          </div>
        </>
      )}
    </div>
  );
};
